import { connectError } from "./errors.js";
import { performHostedFilePartRequest, performHostedFileRequest } from "./hosted-file-request.js";
import { GrantKeyLeaseSet, retainCurrentGrantToken } from "./grant-key-leases.js";
import { LocalFileTransport } from "./local-file-transport.js";
/**
 * Owns every transport concern specific to files. ConnectionTransport only
 * supplies authorization, routing, and proof-signing policy.
 */
export class ConnectionFileTransport {
    options;
    local;
    constructor(options) {
        this.options = options;
        this.local = new LocalFileTransport(options);
    }
    async control(method, path = "", input, signal) {
        const leased = await this.requireFileToken(signal);
        const token = leased.token;
        try {
            if (!token.authority) {
                return await this.local.control(token, method, path, input, signal);
            }
            return await performHostedFileRequest(token, method, path, input, signal, this.options.refreshAuthorization, this.options.authorityProofHeaders, leased.retain);
        }
        finally {
            leased.release();
        }
    }
    async uploadChunk(session, chunkIndex, bytes, signal) {
        const leased = await this.requireFileToken(signal);
        try {
            return await this.local.uploadChunk(session, chunkIndex, bytes, signal, leased.token);
        }
        finally {
            leased.release();
        }
    }
    async downloadChunk(session, chunkIndex, signal) {
        const leased = await this.requireFileToken(signal);
        try {
            return await this.local.downloadChunk(session, chunkIndex, signal, leased.token);
        }
        finally {
            leased.release();
        }
    }
    async downloadHostedPart(session, partIndex, expectedLength, signal) {
        const leased = await this.requireFileToken(signal);
        const token = leased.token;
        try {
            if (!token.authority) {
                throw connectError("not_remote_authority", "Hosted file delivery requires a remote authority endpoint.");
            }
            return await performHostedFilePartRequest(token, `downloads/${encodeURIComponent(session.transfer_id)}/parts/${partIndex}`, expectedLength, signal, this.options.refreshAuthorization, this.options.authorityProofHeaders, leased.retain);
        }
        finally {
            leased.release();
        }
    }
    async requireFileToken(signal) {
        const leases = new GrantKeyLeaseSet(this.options.collectionId, this.options.acquireGrantKeyLease);
        try {
            await retainCurrentGrantToken(this.options.currentToken, leases, signal);
            await this.options.authorizedToken(signal);
            const token = await retainCurrentGrantToken(this.options.currentToken, leases, signal);
            if (!token?.fileCapability) {
                throw connectError("not_authorized", "This connection has no file capability.");
            }
            return {
                token,
                retain: async (_refreshed) => {
                    const current = await retainCurrentGrantToken(this.options.currentToken, leases, signal);
                    if (current)
                        return current;
                    throw connectError("not_authorized", "Reconnect this application to continue.");
                },
                release: () => leases.release()
            };
        }
        catch (error) {
            leases.release();
            throw error;
        }
    }
}
